/**
 * Reorder image IDs so a jumped-to / preloaded slice is fetched FIRST, then its
 * neighbors expanding outward (target, +1, -1, +2, -2, …).
 *
 * The measured root cause of "click a finding, wait ~9.5s, can't scroll" is that
 * the default queue loads frames 0→N in order, leaving the jumped-to slice near
 * the back. Loading the target first makes it the first frame decoded, so it
 * shows immediately and local scrolling is responsive while the rest streams.
 *
 * IMPORTANT: this changes only FETCH/DECODE order, never volume geometry. Use it
 * for the stack path and the background preloader (which load frame-by-frame).
 * Do NOT reorder the imageIds handed to a packed StreamingImageVolume — that
 * array defines spatial order; the volume path must prioritize via load-request
 * priority instead.
 */
export function orderImageIdsForJump(imageIds: readonly string[], targetIndex: number): string[] {
  const n = imageIds.length;
  if (n === 0) return [];
  if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= n) {
    return imageIds.slice();
  }

  const order: string[] = [imageIds[targetIndex]];
  for (let d = 1; d < n; d++) {
    const right = targetIndex + d;
    const left = targetIndex - d;
    if (right < n) order.push(imageIds[right]);
    if (left >= 0) order.push(imageIds[left]);
  }
  return order;
}
